import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import './Alimtalk.css';

const Alimtalk = () => {
  // 상태 관리
  const [alimtalkList, setAlimtalkList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [lastSearchKeyword, setLastSearchKeyword] = useState(''); // 마지막 검색에 사용된 키워드
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 검색 필터 상태
  const [searchFilters, setSearchFilters] = useState({
    customerName: '',
    template: '',
    status: 'ALL'
  });
  const [lastSearchFilters, setLastSearchFilters] = useState({
    customerName: '',
    template: '',
    status: 'ALL'
  });
  
  // 페이징 관련 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // 체크박스 관련 상태
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 폼 데이터
  const [modalForm, setModalForm] = useState({
    userId: '',
    name: '',
    password: ''
  });

  // AbortController 참조
  const abortControllerRef = useRef(null);

  // 검색 핸들러
  const handleSearch = useCallback(async () => {
    // 이전 요청이 있으면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    setIsSearching(true);
    
    try {
      const params = {
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
        name: lastSearchFilters.customerName || "",
        template: lastSearchFilters.template || "",
        status: lastSearchFilters.status || "ALL",
      };
      
      // 백엔드 API 호출
      const response = await apiClient.get('/Alimtalk/GetList', params, abortControllerRef.current.signal);
      
      if (response && response.data) {
        setAlimtalkList(response.data);
        
        // 페이징 정보 업데이트
        setTotalCount(response.totalCount || 0);
        setTotalPages(Math.ceil((response.totalCount || 0) / itemsPerPage));
        
        // 현재 페이지 동기화 (서버에서 받은 페이지 정보가 있다면)
        if (response.nowPage) {
          setCurrentPage(response.nowPage);
        }
        
        // 체크박스 상태 초기화
        setSelectedItems([]);
        setSelectAll(false);
      } else {
        setAlimtalkList([]);
        setTotalCount(0);
        setTotalPages(0);
      }
      
    } catch (error) {
      // AbortError가 아닌 경우에만 알림 표시
      if (error.name !== 'AbortError' && !error.message?.includes('aborted')) {
        alert('사용자 검색에 실패했습니다.');
      }
      
      setAlimtalkList([]);
    } finally {
      setIsSearching(false);
    }
  }, [currentPage, itemsPerPage, lastSearchFilters]);

  // 컴포넌트 마운트 시 전체 사용자 조회 및 페이지/검색 변경 시 재조회
  useEffect(() => {
    handleSearch();
    
    // 컴포넌트 언마운트 시 요청 취소
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleSearch]);

  // 검색 필터 핸들러
  const handleSearchFilters = () => {
    // 검색 필터 업데이트
    setLastSearchFilters({ ...searchFilters });
    // 1페이지로 이동 (useEffect에서 자동 검색 실행)
    setCurrentPage(1);
  };

  // 필터 초기화 핸들러
  const handleResetFilters = () => {
    const resetFilters = {
      customerName: '',
      template: '',
      status: 'ALL'
    };
    setSearchFilters(resetFilters);
    setLastSearchFilters(resetFilters);
    setCurrentPage(1);
  };

  // 재발송 핸들러
  const handleSelectResend = async () => {
    if(selectedItems.length == 0){
      alert('선택된 항목이 없습니다.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await apiClient.post('/Alimtalk/ReSendMessage', selectedItems);
      
      if (response && response.success){
        alert('재발송이 성공적으로 완료되었습니다.');
      
        handleSearch();
      }else{
        alert(response.message);
      }
      
    } catch (error) {
      let errorMessage = '재발송에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // 페이징 렌더링
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 10;
    
    // 현재 페이지를 기준으로 표시할 페이지 범위 계산
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // 끝 페이지가 최대 표시 개수에 못 미치는 경우 시작 페이지 조정
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // 이전 버튼
    pages.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
      >
        &lt;
      </button>
    );

    // 페이지 번호들 (연속적으로 표시)
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    // 다음 버튼
    pages.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    );

    return pages;
  };

  // 체크박스 관련 핸들러
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(alimtalkList.map(alimtalk => alimtalk.idx || alimtalk.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      const newSelectedItems = [...selectedItems, id];
      setSelectedItems(newSelectedItems);
      
      // 모든 항목이 선택되었는지 확인
      const allIds = alimtalkList.map(alimtalk => alimtalk.idx || alimtalk.id);
      if (newSelectedItems.length === allIds.length) {
        setSelectAll(true);
      }
    } else {
      setSelectedItems(prev => prev.filter(item => item !== id));
      setSelectAll(false);
    }
  };

  // 재전송 핸들러
  const handleResend = async (alimtalk) => {
    try{
      const response = await apiClient.post('/Alimtalk/ReSendMessage', [alimtalk.idx]);
      if(response && response.success){
        alert('재전송 요청이 성공적으로 완료되었습니다.');
        handleSearch();
      }else{
        alert(response.message);
      }
    }catch(error){
      console.error('재전송 실패:', error);
    }
  };

  return (
    <div className="alimtalk-page">
      {/* 페이지 헤더 */}
      <div className="page-header">
        <h1>알림톡 관리</h1>
      </div>

      {/* 알림톡 내역 관리 섹션 */}
      <div className="content-section list-section">
        <div className="section-header">
          <h2>알림톡 내역</h2>
          <p>발송된 알림톡 내역을 조회할 수 있습니다.</p>
        </div>

        {/* 검색 필터 섹션 */}
        <div className="search-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>고객명</label>
              <input
                type="text"
                value={searchFilters.customerName}
                onChange={(e) => setSearchFilters(prev => ({
                  ...prev,
                  customerName: e.target.value
                }))}
                placeholder="고객명을 입력하세요"
                onKeyPress={(e) => e.key === 'Enter' && handleSearchFilters()}
              />
            </div>
            
            <div className="filter-group">
              <label>템플릿</label>
              <select
                value={searchFilters.template}
                onChange={(e) => setSearchFilters(prev => ({
                  ...prev,
                  template: e.target.value
                }))}
              >
                <option value="">전체</option>
                <option value="작업별 리포트">작업별 리포트</option>
                <option value="올인원솔루션 리포트">올인원솔루션 리포트</option>
                <option value="광고주등록 알림톡">광고주등록 알림톡</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>상태</label>
              <select
                value={searchFilters.status}
                onChange={(e) => setSearchFilters(prev => ({
                  ...prev,
                  status: e.target.value
                }))}
              >
                <option value="ALL">전체</option>
                <option value="SUCCESS">성공</option>
                <option value="FAIL">실패</option>
                <option value="READY">전송중</option>
              </select>
            </div>
            
            <div className="filter-actions">
              <button 
                className="search-btn"
                onClick={handleSearchFilters}
                disabled={isSearching}
              >
                {isSearching ? '검색 중...' : '검색'}
              </button>
              <button 
                className="reset-btn"
                onClick={handleResetFilters}
              >
                초기화
              </button>
            </div>
          </div>
        </div>

        {/* 목록 컨트롤 */}
        <div className="list-controls">
          <div className="list-info">
            총 {totalCount}건의 알림톡 내역 (페이지 {currentPage} / {totalPages})
            {selectedItems.length > 0 && (
              <span className="selected-count"> • {selectedItems.length}개 선택됨</span>
            )}
          </div>
          <div className="list-actions">
            <button 
              className="action-btn add-btn"
              onClick={handleSelectResend}
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#fff"><path d="M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z"/></svg>
              선택 재발송
            </button>
          </div>
        </div>

        {/* 알림톡 전송 목록 테이블 */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>고객명</th>
                <th>연락처</th>
                <th>템플릿</th>
                <th>컨텐츠 타입</th>
                <th>발송타입</th>
                <th>발송상태</th>
                <th>실패사유</th>
                <th>발송일시</th>
                <th className="action-col">재발송</th>
              </tr>
            </thead>
            <tbody>
              {alimtalkList.length > 0 ? (
                alimtalkList.map((alimtalk, index) => {
                  const alimtalkIdx = alimtalk.idx;
                  return (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(alimtalkIdx)}
                          onChange={(e) => handleSelectItem(alimtalkIdx, e.target.checked)}
                        />
                      </td>
                      <td>{alimtalk.customerName || '-'}</td>
                      <td>{alimtalk.customerCellphone || '-'}</td>
                      <td>{alimtalk.templateName || '-'}</td>
                      <td>{alimtalk.contentType || '-'}</td>
                      <td>{alimtalk.messageType == "kakao" ? "알림톡" : "SMS"}</td>
                      <td>{alimtalk.status == "SUCCESS" ? "전송완료" : alimtalk.status == "FAIL" ? "전송실패" : alimtalk.status == "READY" ? "전송중" : alimtalk.status || '-'}</td>
                      <td>{alimtalk.reason || '-'}</td>
                      <td>{formatDate(alimtalk.regDate)}</td>
                      <td>
                        <button 
                          className="table-action-btn resend-btn"
                          onClick={() => handleResend(alimtalk)}
                          title="재전송"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M500.7 138.7L512 149.4L512 96C512 78.3 526.3 64 544 64C561.7 64 576 78.3 576 96L576 224C576 241.7 561.7 256 544 256L416 256C398.3 256 384 241.7 384 224C384 206.3 398.3 192 416 192L463.9 192L456.3 184.8C456.1 184.6 455.9 184.4 455.7 184.2C380.7 109.2 259.2 109.2 184.2 184.2C109.2 259.2 109.2 380.7 184.2 455.7C259.2 530.7 380.7 530.7 455.7 455.7C463.9 447.5 471.2 438.8 477.6 429.6C487.7 415.1 507.7 411.6 522.2 421.7C536.7 431.8 540.2 451.8 530.1 466.3C521.6 478.5 511.9 490.1 501 501C401 601 238.9 601 139 501C39.1 401 39 239 139 139C238.9 39.1 400.7 39 500.7 138.7z"/></svg>
                        </button>
                      </td>
                      
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    {isSearching ? '검색 중...' : '발송된 알림톡 내역이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="pagination">
            {renderPagination()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Alimtalk;